import type { ApiSchemas } from '@rental/contracts/client'

type Board = ApiSchemas['TodayResponse']

/** The board's lists that something waits on, each with the id of its group on the screen. */
export const ATTENTION = {
  overdue: 'today-overdue',
  overbooked: 'today-overbooked',
  missed: 'today-missed',
  trips: 'today-trips',
  cargos: 'today-cargos',
  fines: 'today-fines',
  money: 'today-money',
  deposits: 'today-deposits',
  documents: 'today-documents',
} as const
export type AttentionKey = keyof typeof ATTENTION

export type AttentionItem = { key: AttentionKey; count: number; tone: 'danger' | 'warning' }

/**
 * UI-1c: what on the board waits on someone, the worst first. Red only where
 * something is wrong that someone must act on (DESIGN.md, "A screen and its
 * parts"): a car past its return, a refused report, a hold about to lapse, a
 * refund that failed, a fine past its deadline, a document in its last week.
 * The rest is orange. Empty when nothing waits. The server already left out
 * what this person may not see.
 */
/**
 * A hand-over's report on Today, in the tone the hand-over and Sistema give it:
 * refused, or stuck on its way, is red; an outcome nobody knows is orange.
 */
export function cargosTone(
  status: Board['compliance']['cargos'][number]['status'],
): 'danger' | 'warning' {
  return status === 'unknown' ? 'warning' : 'danger'
}

export function attention(
  board: Board,
  /** S9: whoever gives the trips out (deliveries.manage) also sees those nobody drives yet. */
  { assignsTrips = false }: { assignsTrips?: boolean } = {},
): AttentionItem[] {
  const items: AttentionItem[] = []
  const add = (key: AttentionKey, count: number, danger: boolean) => {
    if (count > 0) items.push({ key, count, tone: danger ? 'danger' : 'warning' })
  }
  add('overdue', board.overdueReturns.length, true)
  // W2-S S4: a customer paid for a car the category no longer has.
  add('overbooked', board.overbooked.length, true)
  add('missed', board.missedPickups.length, false)
  if (assignsTrips) {
    const open = board.deliveries.filter((trip) => trip.state === 'to_assign')
    // Due already with nobody on it: someone waits at a quay.
    add(
      'trips',
      open.length,
      open.some((trip) => trip.late),
    )
  }
  const { cargos, fines } = board.compliance
  add(
    'cargos',
    cargos.length,
    cargos.some((item) => cargosTone(item.status) === 'danger'),
  )
  add(
    'deposits',
    board.depositAlerts.length,
    board.depositAlerts.some((alert) => alert.level === 'danger'),
  )
  add(
    'money',
    board.moneyToReturn.length,
    board.moneyToReturn.some((item) => item.refundProblem === 'failed'),
  )
  add(
    'fines',
    fines.length,
    fines.some((fine) => fine.state === 'overdue'),
  )
  add(
    'documents',
    board.expiringDocuments.length,
    board.expiringDocuments.some((document) => document.daysLeft <= 7),
  )
  return [
    ...items.filter((item) => item.tone === 'danger'),
    ...items.filter((item) => item.tone === 'warning'),
  ]
}
